// Phase 8 → 9.4a — Layout shared across every authenticated route.
//
// Header structure (Phase 9.4a rewrite):
//   Row 1 — brand row:
//     [ goStops logo ] › [ tool crumb ]              [ avatar · email │ Logout ]
//   Row 2 — sub-nav (only on tool routes):
//     [ Dashboard · Reorder · Purchase Orders · BOM · Stock Matrix · Items · Locations · Master Data ]
//     Horizontally scrollable on narrow viewports, whitespace-nowrap
//     on every item, active item gets the brand red pill.
//
// Deliberately: the breadcrumb intentionally OMITS the current page
// name — the sub-nav already highlights it. Fixes the previous
// duplicate-"Dashboard" bug.

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Settings as SettingsIcon } from "lucide-react";
import { api, detailFromError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

// Nav items relative to the Amazon-invoices tool.
const TOOL_NAV_ITEMS = [
  { to: "/tools/amazon-invoices/new", label: "Run", testid: "nav-run" },
  { to: "/tools/amazon-invoices", label: "History", testid: "nav-history", end: true },
  { to: "/tools/amazon-invoices/settings", label: "Settings", testid: "nav-settings" },
];

// Phase 9.0 — F&B tool nav.
const FNB_NAV_ITEMS = [
  { to: "/tools/fnb-po", label: "Dashboard", testid: "fnb-nav-dashboard", end: true },
  { to: "/tools/fnb-po/reorder", label: "Reorder", testid: "fnb-nav-reorder-tab" },
  { to: "/tools/fnb-po/pos", label: "Purchase Orders", testid: "fnb-nav-pos-tab" },
  { to: "/tools/fnb-po/bom", label: "BOM", testid: "fnb-nav-bom-tab" },
  { to: "/tools/fnb-po/stock-matrix", label: "Stock Matrix", testid: "fnb-nav-matrix-tab" },
  { to: "/tools/fnb-po/items", label: "Items", testid: "fnb-nav-items-tab" },
  { to: "/tools/fnb-po/locations", label: "Locations", testid: "fnb-nav-locations-tab" },
  { to: "/tools/fnb-po/bar-stock", label: "Bar Stock", testid: "fnb-nav-bar-stock-tab" },
  { to: "/tools/fnb-po/merchandise", label: "Merchandise", testid: "fnb-nav-merchandise-tab" },
  { to: "/tools/fnb-po/master-data", label: "Master Data", testid: "fnb-nav-master-data-tab" },
];

function inAmazonTool(pathname) {
  return pathname.startsWith("/tools/amazon-invoices");
}

function inFnbTool(pathname) {
  return pathname.startsWith("/tools/fnb-po");
}

// Phase 8.1 — user avatar. Prefers the Google `picture` URL; falls
// back to a two-letter initials chip drawn from the name/email.
function UserAvatar({ user }) {
  const initials = (() => {
    const name = user.name || user.email || "";
    const parts = name.replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return "??";
  })();
  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        referrerPolicy="no-referrer"
        className="h-8 w-8 rounded-full object-cover ring-2 ring-brand-subtle"
        data-testid="user-avatar"
      />
    );
  }
  return (
    <div
      className="h-8 w-8 rounded-full bg-neutral-900 text-white text-[11px] font-semibold flex items-center justify-center ring-2 ring-brand-subtle"
      data-testid="user-avatar-initials"
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// Breadcrumb WITHOUT the current-page label — the sub-nav shows the
// active page, no need to duplicate it in the crumb.
function toolNameForPath(pathname) {
  if (pathname.startsWith("/tools/amazon-invoices")) return "Amazon Invoice Downloader";
  if (pathname.startsWith("/tools/fnb-po")) return "F&B Stock & PO Release";
  return null;
}

export default function Layout() {
  const [settings, setSettings] = useState(null);
  const [bootChecked, setBootChecked] = useState(false);
  const [health, setHealth] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/settings");
        if (!cancelled) {
          setSettings(res.data);
          if (inAmazonTool(location.pathname) && !res.data.has_cookies) {
            if (
              location.pathname !== "/tools/amazon-invoices/settings"
              && !location.pathname.match(/^\/tools\/amazon-invoices\/[^/]+$/)
            ) {
              navigate("/tools/amazon-invoices/settings", { replace: true });
            }
          }
        }
      } catch (e) {
        if (!cancelled) setSettings({ has_cookies: false });
        console.warn("settings load failed:", detailFromError(e));
      } finally {
        if (!cancelled) setBootChecked(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const res = await api.get("/system/health");
        if (!cancelled) setHealth(res.data);
      } catch {
        // best-effort probe
      }
    };
    probe();
    const t = setInterval(probe, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const playwrightOk = health?.playwright?.ok !== false;
  const playwrightError = health?.playwright?.error;
  const toolName = toolNameForPath(location.pathname);
  const showToolNav = inAmazonTool(location.pathname);
  const showFnbNav = inFnbTool(location.pathname);
  const activeToolNav = showFnbNav ? FNB_NAV_ITEMS : TOOL_NAV_ITEMS;
  const showSubNav = showToolNav || showFnbNav;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="top-right" richColors />
      {inAmazonTool(location.pathname) && !playwrightOk && (
        <div
          data-testid="playwright-health-banner"
          className="border-b border-destructive/40 bg-destructive/10 text-destructive px-6 py-2 text-sm"
        >
          <span className="font-medium">⚠ Backend can&apos;t launch Chromium —</span>{" "}
          admin needs to run{" "}
          <code className="font-mono text-xs bg-destructive/10 px-1 py-0.5 rounded">
            PLAYWRIGHT_BROWSERS_PATH=/pw-browsers playwright install chromium
          </code>{" "}
          then restart the backend.
          {playwrightError && (
            <span className="text-xs ml-2 opacity-70">({String(playwrightError).slice(0, 120)})</span>
          )}
        </div>
      )}

      <header className="bg-white border-b border-neutral-200 shadow-sm">
        {/* --- Row 1: brand + breadcrumb + user ---------------------- */}
        <div className="mx-auto max-w-7xl px-6 py-2 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center select-none shrink-0"
            data-testid="brand"
            aria-label="goStops — home"
          >
            <img
              src="/gostops-logo.svg"
              alt="goStops"
              className="h-8 w-auto"
              data-testid="brand-logo"
            />
          </Link>

          {toolName && (
            <nav
              aria-label="Breadcrumb"
              className="hidden sm:flex items-center gap-2 text-sm text-neutral-500 whitespace-nowrap min-w-0"
              data-testid="breadcrumbs"
            >
              <span className="opacity-50">›</span>
              <span className="text-neutral-700 truncate">{toolName}</span>
            </nav>
          )}

          <div className="flex-1" />

          {showToolNav && bootChecked && (
            <Badge
              variant={settings?.has_cookies ? "secondary" : "destructive"}
              data-testid="session-badge"
              className="shrink-0"
            >
              {settings?.has_cookies ? "session ready" : "no cookies"}
            </Badge>
          )}

          {user && typeof user === "object" && (
            <div className="flex items-center gap-3 shrink-0">
              <UserAvatar user={user} />
              <span
                className="text-sm text-neutral-700 hidden md:inline whitespace-nowrap"
                data-testid="user-email"
              >
                {user.email}
              </span>
              {/* Phase 2 — Admin gear. Routes to /admin, which lands on the
                  Admin tools landing page (currently: API Keys). Hidden
                  nowhere today because every seeded user has role=admin;
                  the forward hook is baked in server-side via require_admin. */}
              <Link
                to="/admin"
                data-testid="admin-gear"
                aria-label="Admin"
                title="Admin"
                className="hidden md:inline-flex items-center justify-center h-8 w-8 rounded-full text-neutral-600 hover:text-brand hover:bg-brand-muted transition-colors"
              >
                <SettingsIcon className="h-4 w-4" />
              </Link>
              <span
                className="hidden md:inline h-6 border-l border-neutral-200"
                aria-hidden="true"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-button"
                className="text-neutral-600 hover:text-brand hover:bg-brand-muted"
              >
                Logout
              </Button>
            </div>
          )}
        </div>

        {/* --- Row 2: sub-nav ---------------------------------------- */}
        {showSubNav && (
          <div className="border-t border-neutral-100">
            <div className="mx-auto max-w-7xl px-6">
              <nav
                className="flex items-center gap-1 py-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                aria-label="Tool navigation"
                data-testid="tool-subnav"
              >
                {activeToolNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testid}
                    className={({ isActive }) =>
                      `whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium transition-colors shrink-0 ${
                        isActive
                          ? "bg-brand text-white shadow-sm"
                          : "text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
